import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { dayShort, colorOf, fmtRange } from "../../lib/content";
import { useLang } from "../../lib/i18n";

const BatchRow = ({ cls, i, onOpen }) => {
  const c = colorOf(cls.color);
  const { lang, pick } = useLang();
  return (
    <Reveal delay={i * 0.08}>
      <button onClick={() => onOpen(cls)} data-testid={`program-row-${cls.id}`}
        className="group grid w-full gap-4 border-t border-white/[0.08] py-6 text-start sm:grid-cols-[1fr_auto] sm:items-center">
        <div className="flex items-start gap-4">
          <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full" style={{ background: c, boxShadow: `0 0 16px ${c}` }} />
          <div>
            <h3 className="font-display text-2xl font-bold text-white transition-colors group-hover:text-[color:var(--c)] sm:text-3xl" style={{ "--c": c }}>{pick(cls, "title")}</h3>
            <span className="mt-1 inline-block rounded-full border px-2.5 py-0.5 font-mono text-[12px]" style={{ borderColor: `${c}66`, color: c }}>{pick(cls, "tag")}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 ps-7 sm:ps-0">
          {cls.sessions.map((s, k) => (
            <span key={k} className="rounded-lg bg-white/[0.05] px-3 py-2 font-mono text-[13px] text-zinc-200">
              <span className="text-zinc-400">{lang === "ar" ? dayShort(s.day, lang) : s.day.toUpperCase()}</span> {fmtRange(s, lang)}
            </span>
          ))}
          <ArrowUpRight size={18} className="text-zinc-500 transition-transform group-hover:-translate-y-0.5 group-hover:text-white rtl:-scale-x-100" />
        </div>
      </button>
    </Reveal>
  );
};

export const Programs = ({ classes, onOpen, image }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const batches = classes.filter((c) => c.program === "dillusions");
  const { t } = useLang();
  return (
    <section id="programs" ref={ref} className="mx-auto max-w-[1440px] px-5 py-24 lg:px-10 lg:py-32" data-testid="programs-section">
      <SectionHead eyebrow={t.progEyebrow} title={<>{t.progTitle[0]}<br />{t.progTitle[1]}</>}>
        {t.progText}
      </SectionHead>
      <Reveal className="mb-10 lg:mb-14">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3" data-testid="programs-banner">
          {t.progBanner.map((w, i) => (
            <span key={w} className={`rounded-full px-4 py-2 font-display text-sm font-bold uppercase tracking-tight sm:px-5 sm:text-lg ${i === 0 ? "bg-accent text-onaccent" : "border border-white/15 bg-white/[0.04] text-white"}`}>{w}</span>
          ))}
        </div>
      </Reveal>
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
        <Reveal className="lg:col-span-5">
          <div className="clip-frame relative aspect-[4/5] overflow-hidden rounded-2xl">
            <motion.img style={{ y, scale: 1.18 }} src={image || undefined} loading="lazy" decoding="async" alt="Dillusions dancers in rehearsal" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-5 start-5 end-5 flex items-end justify-between">
              <span className="font-display text-2xl font-bold uppercase leading-none text-white">{t.trainTwice[0]}<br />{t.trainTwice[1]}<br />{t.trainTwice[2]}</span>
              <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-accent">{t.batches(batches.length)}</span>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <div className="mb-2 hidden grid-cols-[1fr_auto] font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 sm:grid">
            <span>{t.groupAge}</span><span>{t.schedTimings}</span>
          </div>
          {batches.map((c, i) => <BatchRow key={c.id} cls={c} i={i} onOpen={onOpen} />)}
          <div className="border-t border-white/[0.08]" />
        </div>
      </div>
    </section>
  );
};
